import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language, PlanetName } from '../types/kundali';
import { 
  buildCompletePlanetaryBhaveshMatrix 
} from '../utils/planetaryMatrixEngine';
import { 
  PlanetaryMatrixEntry, 
  BhaveshMatrixEntry 
} from '../types/planetaryMatrix';
import { 
  getPlanetFullName, 
  getPlanetShortCode, 
  getZodiacName 
} from '../utils/language';
import {
  Sparkles,
  Compass,
  Star,
  Eye,
  Info,
  ChevronDown,
  ChevronUp,
  Shield,
  Award,
  Zap,
  Layers,
  ArrowRight,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
  Flame,
  Activity,
  Maximize2
} from 'lucide-react';

interface PlanetaryBhaveshMatrixViewProps {
  kundali: CompleteKundali;
  lang?: Language;
  onSelectHouse?: (houseNumber: number) => void;
}

export const PlanetaryBhaveshMatrixView: React.FC<PlanetaryBhaveshMatrixViewProps> = ({
  kundali,
  lang = 'en',
  onSelectHouse
}) => {
  const isHi = lang === 'hi';
  const [activeTab, setActiveTab] = useState<'PLANETS' | 'BHAVAS'>('PLANETS');
  const [expandedPlanet, setExpandedPlanet] = useState<PlanetName | null>(null);
  const [expandedHouse, setExpandedHouse] = useState<number | null>(null);
  const [planetFilter, setPlanetFilter] = useState<'ALL' | 'BENEFIC' | 'MALEFIC' | 'ACTIVE_DASHA'>('ALL');
  const [houseFilter, setHouseFilter] = useState<'ALL' | 'KENDRA' | 'TRIKONA' | 'DUSTHANA' | 'ACTIVE_DASHA'>('ALL');

  // Compute structured deterministic data
  const matrixData = useMemo(() => {
    return buildCompletePlanetaryBhaveshMatrix(kundali, lang);
  }, [kundali, lang]);

  const filteredPlanets = useMemo(() => {
    return matrixData.planets.filter(p => {
      if (planetFilter === 'BENEFIC') return p.naturalNature === 'Natural Benefic' || p.functionalRole.isFunctionalBenefic;
      if (planetFilter === 'MALEFIC') return p.naturalNature === 'Natural Malefic' || p.functionalRole.isFunctionalMalefic;
      if (planetFilter === 'ACTIVE_DASHA') return p.dashaStatus.isMahadashaActive || p.dashaStatus.isAntardashaActive || p.dashaStatus.isPratyantardashaActive;
      return true;
    });
  }, [matrixData.planets, planetFilter]);

  const filteredHouses = useMemo(() => {
    return matrixData.bhaveshMatrix.filter(b => {
      if (houseFilter === 'KENDRA') return [1, 4, 7, 10].includes(b.houseNumber);
      if (houseFilter === 'TRIKONA') return [1, 5, 9].includes(b.houseNumber);
      if (houseFilter === 'DUSTHANA') return [6, 8, 12].includes(b.houseNumber);
      if (houseFilter === 'ACTIVE_DASHA') return b.dashaActivation.isActive;
      return true;
    });
  }, [matrixData.bhaveshMatrix, houseFilter]);

  const getDignityBadgeClass = (dignityLevel: string) => {
    if (dignityLevel.includes('Exalted') || dignityLevel.includes('Moolatrikona') || dignityLevel.includes('Own')) {
      return 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700 font-bold';
    }
    if (dignityLevel.includes('Friend')) {
      return 'bg-teal-50 dark:bg-teal-950/30 text-teal-800 dark:text-teal-300 border-teal-200 dark:border-teal-700';
    }
    if (dignityLevel.includes('Debilitated')) {
      return 'bg-rose-100 dark:bg-rose-950/40 text-rose-900 dark:text-rose-300 border-rose-300 dark:border-rose-700 font-bold';
    }
    if (dignityLevel.includes('Enemy')) {
      return 'bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-300 border-amber-200 dark:border-amber-700';
    }
    return 'bg-astro-card text-astro-text border-astro-border';
  };

  return (
    <div className="bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-5 shadow-sm space-y-4">
      {/* 1. Header Bar with Dual-View Switcher */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-astro-border pb-4">
        <div>
          <div className="flex items-center gap-2">
            <Compass className="w-5 h-5 text-astro-primary" />
            <h3 className="font-bold text-astro-primary font-serif text-lg leading-tight">
              {isHi ? 'ग्रह एवं भावेश स्थिति सारणी (Vedic Deterministic Matrix)' : 'Planetary Dignity & Bhavesh Condition Matrix'}
            </h3>
          </div>
          <p className="text-xs text-astro-secondary-text mt-0.5">
            {isHi 
              ? 'ग्रहों के स्वामित्व, भाव स्थिति, दृष्टि व संबंध का स्पष्ट शास्त्रीय विश्लेषण' 
              : 'Deterministic Jyotish data separating planetary condition from house lordship placement'}
          </p>
        </div>

        {/* View Mode Switcher Pills */}
        <div className="flex items-center gap-1 bg-astro-card p-1 rounded-xl border border-astro-border self-start md:self-auto">
          <button
            type="button"
            onClick={() => setActiveTab('PLANETS')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'PLANETS'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-text'
            }`}
          >
            <Star className="w-3.5 h-3.5" />
            <span>{isHi ? '१. ग्रहीय मैट्रिक्स (Planets)' : '1. Planetary Matrix'}</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('BHAVAS')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'BHAVAS'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-text'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>{isHi ? '२. भावेश मैट्रिक्स (12 Houses)' : '2. Bhavesh Matrix'}</span>
          </button>
        </div>
      </div>

      {/* 2. Executive Quick Key Metrics Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs">
        <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
          <span className="text-astro-secondary-text block text-[10px] uppercase font-semibold">
            {isHi ? 'लग्न / लग्नेश' : 'Ascendant / Lagnesh'}
          </span>
          <span className="font-bold text-astro-primary text-sm">
            {matrixData.ascendantSign} • {matrixData.summary.lagnesh} ({matrixData.summary.lagneshPlacement})
          </span>
        </div>
        <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
          <span className="text-astro-secondary-text block text-[10px] uppercase font-semibold">
            {isHi ? 'धनेश (2L) व कर्मेश (10L)' : 'Dhanesh (2L) & Karmesh (10L)'}
          </span>
          <span className="font-bold text-astro-text text-sm">
            {matrixData.summary.dhanesh} (2L) • {matrixData.summary.karmesh} (10L)
          </span>
        </div>
        <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
          <span className="text-astro-secondary-text block text-[10px] uppercase font-semibold">
            {isHi ? 'योगकारक / भाग्येश' : 'Yogakaraka / Bhagyesh'}
          </span>
          <span className="font-bold text-astro-accent text-sm">
            {matrixData.summary.yogakaraka ? `${matrixData.summary.yogakaraka} (Yogakaraka)` : `None • ${matrixData.summary.bhagyesh} (9L)`}
          </span>
        </div>
        <div className="p-2.5 bg-astro-card rounded-xl border border-astro-border">
          <span className="text-astro-secondary-text block text-[10px] uppercase font-semibold">
            {isHi ? 'उच्च / नीच ग्रह' : 'Exalted / Debilitated'}
          </span>
          <span className="font-semibold text-astro-text text-xs">
            {matrixData.summary.exaltedPlanets.length > 0 ? `↑ ${matrixData.summary.exaltedPlanets.join(', ')}` : 'None'} • {matrixData.summary.debilitatedPlanets.length > 0 ? `↓ ${matrixData.summary.debilitatedPlanets.join(', ')}` : 'None'}
          </span>
        </div>
      </div>

      {/* 3. TAB A: PLANETARY MATRIX */}
      {activeTab === 'PLANETS' && (
        <div className="space-y-3">
          {/* Quick Filters */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
              <span className="text-[10px] font-bold text-astro-secondary-text uppercase mr-1">
                {isHi ? 'फिल्टर:' : 'Filter:'}
              </span>
              <button
                type="button"
                onClick={() => setPlanetFilter('ALL')}
                className={`px-2.5 py-0.5 rounded-lg font-bold border transition-all cursor-pointer ${
                  planetFilter === 'ALL'
                    ? 'bg-astro-primary text-white border-astro-primary shadow-2xs'
                    : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-primary'
                }`}
              >
                {isHi ? `सभी ग्रह (${matrixData.planets.length})` : `All (${matrixData.planets.length})`}
              </button>
              <button
                type="button"
                onClick={() => setPlanetFilter('BENEFIC')}
                className={`px-2.5 py-0.5 rounded-lg font-bold border transition-all cursor-pointer ${
                  planetFilter === 'BENEFIC'
                    ? 'bg-astro-primary text-white border-astro-primary shadow-2xs'
                    : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-primary'
                }`}
              >
                ✨ {isHi ? 'शुभ ग्रह' : 'Benefics'}
              </button>
              <button
                type="button"
                onClick={() => setPlanetFilter('MALEFIC')}
                className={`px-2.5 py-0.5 rounded-lg font-bold border transition-all cursor-pointer ${
                  planetFilter === 'MALEFIC'
                    ? 'bg-astro-primary text-white border-astro-primary shadow-2xs'
                    : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-primary'
                }`}
              >
                🛡️ {isHi ? 'क्रूर/पाप ग्रह' : 'Malefics'}
              </button>
              <button
                type="button"
                onClick={() => setPlanetFilter('ACTIVE_DASHA')}
                className={`px-2.5 py-0.5 rounded-lg font-bold border transition-all cursor-pointer ${
                  planetFilter === 'ACTIVE_DASHA'
                    ? 'bg-astro-primary text-white border-astro-primary shadow-2xs'
                    : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-primary'
                }`}
              >
                ⚡ {isHi ? 'सक्रिय दशा' : 'Active Dasha'}
              </button>
            </div>

            <span className="text-[10px] text-astro-secondary-text italic hidden sm:inline">
              {isHi ? 'विस्तृत विवरण देखने हेतु पंक्ति पर क्लिक करें' : 'Click any row for complete 16-field forensic inspection'}
            </span>
          </div>

          {/* Main Table */}
          <div className="w-full overflow-x-auto custom-horizontal-scrollbar rounded-xl border border-astro-border bg-white dark:bg-astro-card/80 shadow-2xs">
            <table className="w-full min-w-[860px] text-left border-collapse text-xs">
              <thead>
                <tr className="bg-astro-card/90 border-b border-astro-border text-astro-secondary-text uppercase tracking-wider text-[10px]">
                  <th className="py-2.5 px-3 font-bold w-[16%]">{isHi ? 'ग्रह' : 'Planet'}</th>
                  <th className="py-2.5 px-2 font-bold w-[13%]">{isHi ? 'कार्यात्मक भूमिका' : 'Functional Role'}</th>
                  <th className="py-2.5 px-2 font-bold w-[12%]">{isHi ? 'स्वामित्व' : 'Lordship'}</th>
                  <th className="py-2.5 px-2 font-bold w-[13%]">{isHi ? 'स्थिति (Placement)' : 'Placement'}</th>
                  <th className="py-2.5 px-2 font-bold w-[11%]">{isHi ? 'भावेश स्थिति' : 'Lordship → Pl.'}</th>
                  <th className="py-2.5 px-2 font-bold w-[12%]">{isHi ? 'गरिमा (Dignity)' : 'Dignity'}</th>
                  <th className="py-2.5 px-2 font-bold w-[14%]">{isHi ? 'राशि स्वामी संबंध' : 'Planet → Sign Lord'}</th>
                  <th className="py-2.5 px-2 font-bold w-[9%] text-right">{isHi ? 'विवरण' : 'Details'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-astro-border/60">
                {filteredPlanets.map((p) => {
                  const isExpanded = expandedPlanet === p.planet;

                  return (
                    <React.Fragment key={p.planet}>
                      <tr 
                        onClick={() => setExpandedPlanet(isExpanded ? null : p.planet)}
                        className={`hover:bg-astro-primary/5 transition-colors cursor-pointer ${
                          isExpanded ? 'bg-astro-primary/10' : ''
                        }`}
                      >
                        {/* Planet Name + Nature Badge + Dasha Badge */}
                        <td className="py-2.5 px-3 font-bold text-astro-text">
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded-md font-bold flex items-center justify-center text-[10px] shrink-0 border ${
                              p.dignity.isExalted 
                                ? 'bg-emerald-100 text-emerald-900 border-emerald-400 font-extrabold' 
                                : p.dignity.isDebilitated 
                                ? 'bg-rose-100 text-rose-900 border-rose-400 font-extrabold'
                                : p.dignity.isOwnSign
                                ? 'bg-teal-50 text-teal-800 border-teal-300'
                                : 'bg-astro-primary/10 border-astro-primary/20 text-astro-primary'
                            }`}>
                              <span>{getPlanetShortCode(p.planet, lang)}</span>
                            </div>

                            <div className="flex flex-col">
                              <div className="flex items-center gap-1">
                                <span className="font-bold text-astro-primary">{getPlanetFullName(p.planet, lang)}</span>
                                {p.conditions.isRetrograde && (
                                  <span className="text-[9px] text-astro-secondary font-bold" title="Retrograde (Vakri)">(R)</span>
                                )}
                                {p.conditions.isCombust && (
                                  <span className="text-[9px] text-rose-600 font-bold" title="Combust (Asta)">🔥</span>
                                )}
                              </div>
                              <span className="text-[9px] text-astro-secondary-text font-normal">
                                {isHi ? p.naturalNatureHi : p.naturalNature}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Functional Role */}
                        <td className="py-2.5 px-2">
                          <div className="flex flex-col">
                            <span className="font-bold text-astro-text text-[11px]">
                              {isHi ? p.functionalRole.titleHi : p.functionalRole.titleEn}
                            </span>
                            {p.functionalRole.isYogakaraka && (
                              <span className="text-[9px] font-bold text-amber-700 bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800 px-1 py-0.2 rounded w-fit mt-0.5">
                                ★ {isHi ? 'योगकारक' : 'Yogakaraka'}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Lordship */}
                        <td className="py-2.5 px-2 font-mono text-[11px]">
                          <span className="px-1.5 py-0.5 rounded bg-astro-card border border-astro-border text-astro-text font-semibold">
                            {isHi ? p.lordship.formattedHi : p.lordship.formattedEn}
                          </span>
                        </td>

                        {/* Placement */}
                        <td className="py-2.5 px-2 font-mono text-[11px]">
                          <div className="flex flex-col">
                            <span className="font-bold text-astro-primary">
                              {p.placement.house}H • {getZodiacName(p.placement.sign, lang)}
                            </span>
                            <span className="text-[10px] text-astro-secondary-text">
                              {p.placement.formattedDegree}
                            </span>
                          </div>
                        </td>

                        {/* Lordship -> Placement Vector */}
                        <td className="py-2.5 px-2">
                          <span className={`px-2 py-0.5 rounded-md font-mono font-bold text-[11px] border inline-flex items-center gap-1 ${
                            p.lordshipPlacement.isOwnHousePlacement
                              ? 'bg-emerald-50 text-emerald-900 border-emerald-300'
                              : 'bg-indigo-50 dark:bg-indigo-950/30 text-indigo-900 dark:text-indigo-200 border-indigo-200 dark:border-indigo-800'
                          }`}>
                            <span>{isHi ? p.lordshipPlacement.vectorHi : p.lordshipPlacement.vectorEn}</span>
                          </span>
                        </td>

                        {/* Dignity */}
                        <td className="py-2.5 px-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] border inline-block ${getDignityBadgeClass(p.dignity.level)}`}>
                            {p.dignity.isExalted && '↑ '}
                            {p.dignity.isDebilitated && '↓ '}
                            {p.dignity.level}
                          </span>
                        </td>

                        {/* Planet -> Sign Lord Relationship */}
                        <td className="py-2.5 px-2 text-[10.5px]">
                          <div className="flex flex-col">
                            <span className="font-semibold text-astro-text">
                              {isHi ? p.signLordRelationship.directionHi : p.signLordRelationship.directionEn}
                            </span>
                            <span className="text-[9.5px] text-astro-secondary-text">
                              {isHi ? 'राशि स्वामी: ' : 'Lord: '} {p.signLord}
                            </span>
                          </div>
                        </td>

                        {/* Details Expand Toggle */}
                        <td className="py-2.5 px-2 text-right">
                          <button
                            type="button"
                            className="p-1 rounded-lg hover:bg-astro-border text-astro-primary transition-colors inline-flex items-center"
                            title={isExpanded ? 'Collapse' : 'Expand detailed 16-field forensic card'}
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>

                      {/* Expanded Forensic Detail Drawer for Planet */}
                      {isExpanded && (
                        <tr className="bg-astro-primary/5">
                          <td colSpan={8} className="p-4 border-b border-astro-border">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                              {/* Card 1: Astronomical & Navamsha Coordinates */}
                              <div className="p-3 bg-white dark:bg-astro-card rounded-xl border border-astro-border space-y-1.5">
                                <span className="font-bold text-astro-primary uppercase tracking-wider text-[10px] block flex items-center gap-1">
                                  <Compass className="w-3.5 h-3.5 text-astro-accent" />
                                  {isHi ? 'खगोलीय व नवमांश स्थिति' : 'Astronomical & Navamsha'}
                                </span>
                                <div className="space-y-1 text-[11px]">
                                  <div className="flex justify-between">
                                    <span className="text-astro-secondary-text">{isHi ? 'D1 राशि व अंश:' : 'D1 Position:'}</span>
                                    <span className="font-mono font-bold text-astro-text">{p.placement.sign} {p.placement.formattedDegree}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-astro-secondary-text">{isHi ? 'D9 नवमांश:' : 'D9 Navamsha:'}</span>
                                    <span className="font-mono font-bold text-indigo-800 dark:text-indigo-300">
                                      {p.d9Navamsha.sign} ({p.d9Navamsha.lord}) {p.d9Navamsha.isVargottama ? '★ Vargottama' : ''}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-astro-secondary-text">{isHi ? 'नक्षत्र व चरण:' : 'Nakshatra & Pada:'}</span>
                                    <span className="font-semibold text-astro-text">{p.nakshatra.formatted} (Lord: {p.nakshatra.lord})</span>
                                  </div>
                                </div>
                              </div>

                              {/* Card 2: Strength, BAV & Condition Metrics */}
                              <div className="p-3 bg-white dark:bg-astro-card rounded-xl border border-astro-border space-y-1.5">
                                <span className="font-bold text-astro-primary uppercase tracking-wider text-[10px] block flex items-center gap-1">
                                  <Activity className="w-3.5 h-3.5 text-astro-accent" />
                                  {isHi ? 'षड्बल, BAV व अवस्था' : 'Strength, BAV & Condition'}
                                </span>
                                <div className="space-y-1 text-[11px]">
                                  <div className="flex justify-between">
                                    <span className="text-astro-secondary-text">{isHi ? 'षड्बल सामर्थ्य:' : 'Shadbala:'}</span>
                                    <span className="font-mono font-bold text-astro-primary">{p.shadbala.totalRupas.toFixed(2)} R ({p.shadbala.ratio.toFixed(2)}x Ratio)</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-astro-secondary-text">{isHi ? 'BAV बिंदु (इस राशि में):' : 'BAV Points in Sign:'}</span>
                                    <span className="font-mono font-bold text-emerald-800 dark:text-emerald-300">{p.bav.formatted} Bindus</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-astro-secondary-text">{isHi ? 'अवस्था एवं गति:' : 'Conditions:'}</span>
                                    <span className="font-semibold text-astro-text">{p.conditions.flagsEn.join(', ')}</span>
                                  </div>
                                </div>
                              </div>

                              {/* Card 3: Outbound Graha Drishti */}
                              <div className="p-3 bg-white dark:bg-astro-card rounded-xl border border-astro-border space-y-1.5">
                                <span className="font-bold text-astro-primary uppercase tracking-wider text-[10px] block flex items-center gap-1">
                                  <Eye className="w-3.5 h-3.5 text-astro-accent" />
                                  {isHi ? 'ग्रह दृष्टि (Drishti on Houses)' : 'Graha Drishti (Aspects Given)'}
                                </span>
                                <div className="space-y-1 text-[11px]">
                                  <div className="flex justify-between">
                                    <span className="text-astro-secondary-text">{isHi ? 'दृष्टि भाव:' : 'Aspects Cast On:'}</span>
                                    <span className="font-mono font-bold text-astro-primary">{p.drishti.formattedEn}</span>
                                  </div>
                                  <div className="text-[10px] text-astro-secondary-text leading-tight pt-1 border-t border-astro-border/50">
                                    {isHi 
                                      ? `${p.planet} ${p.placement.house} भाव में स्थित होकर ${p.drishti.formattedHi} पर प्रभाव डाल रहा है।`
                                      : `${p.planet} stationed in House ${p.placement.house} actively projects energy into ${p.drishti.formattedEn}.`}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 4. TAB B: 12-HOUSE BHAVESH MATRIX */}
      {activeTab === 'BHAVAS' && (
        <div className="space-y-3">
          {/* Quick House Filters */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
              <span className="text-[10px] font-bold text-astro-secondary-text uppercase mr-1">
                {isHi ? 'भाव फिल्टर:' : 'Filter:'}
              </span>
              <button
                type="button"
                onClick={() => setHouseFilter('ALL')}
                className={`px-2.5 py-0.5 rounded-lg font-bold border transition-all cursor-pointer ${
                  houseFilter === 'ALL'
                    ? 'bg-astro-primary text-white border-astro-primary shadow-2xs'
                    : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-primary'
                }`}
              >
                {isHi ? `सभी १२ भाव (12)` : `All 12 Houses`}
              </button>
              <button
                type="button"
                onClick={() => setHouseFilter('KENDRA')}
                className={`px-2.5 py-0.5 rounded-lg font-bold border transition-all cursor-pointer ${
                  houseFilter === 'KENDRA'
                    ? 'bg-astro-primary text-white border-astro-primary shadow-2xs'
                    : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-primary'
                }`}
              >
                🏛️ {isHi ? 'केंद्र भाव (1,4,7,10)' : 'Kendra (1,4,7,10)'}
              </button>
              <button
                type="button"
                onClick={() => setHouseFilter('TRIKONA')}
                className={`px-2.5 py-0.5 rounded-lg font-bold border transition-all cursor-pointer ${
                  houseFilter === 'TRIKONA'
                    ? 'bg-astro-primary text-white border-astro-primary shadow-2xs'
                    : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-primary'
                }`}
              >
                🔺 {isHi ? 'त्रिकोण भाव (1,5,9)' : 'Trikona (1,5,9)'}
              </button>
              <button
                type="button"
                onClick={() => setHouseFilter('DUSTHANA')}
                className={`px-2.5 py-0.5 rounded-lg font-bold border transition-all cursor-pointer ${
                  houseFilter === 'DUSTHANA'
                    ? 'bg-astro-primary text-white border-astro-primary shadow-2xs'
                    : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-primary'
                }`}
              >
                ⚡ {isHi ? 'त्रिक भाव (6,8,12)' : 'Dusthana (6,8,12)'}
              </button>
              <button
                type="button"
                onClick={() => setHouseFilter('ACTIVE_DASHA')}
                className={`px-2.5 py-0.5 rounded-lg font-bold border transition-all cursor-pointer ${
                  houseFilter === 'ACTIVE_DASHA'
                    ? 'bg-astro-primary text-white border-astro-primary shadow-2xs'
                    : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-primary'
                }`}
              >
                ⚡ {isHi ? 'दशा सक्रिय' : 'Active in Dasha'}
              </button>
            </div>
          </div>

          {/* Bhavesh Table */}
          <div className="w-full overflow-x-auto custom-horizontal-scrollbar rounded-xl border border-astro-border bg-white dark:bg-astro-card/80 shadow-2xs">
            <table className="w-full min-w-[900px] text-left border-collapse text-xs">
              <thead>
                <tr className="bg-astro-card/90 border-b border-astro-border text-astro-secondary-text uppercase tracking-wider text-[10px]">
                  <th className="py-2.5 px-3 font-bold w-[12%]">{isHi ? 'भाव' : 'House'}</th>
                  <th className="py-2.5 px-2 font-bold w-[10%]">{isHi ? 'राशि' : 'Sign'}</th>
                  <th className="py-2.5 px-2 font-bold w-[11%]">{isHi ? 'भावेश (Lord)' : 'Bhavesh'}</th>
                  <th className="py-2.5 px-2 font-bold w-[14%]">{isHi ? 'भावेश स्थिति' : 'Lord Placement'}</th>
                  <th className="py-2.5 px-2 font-bold w-[12%]">{isHi ? 'भावेश गरिमा' : 'Lord Dignity'}</th>
                  <th className="py-2.5 px-2 font-bold w-[11%]">{isHi ? 'स्थित ग्रह' : 'Occupants'}</th>
                  <th className="py-2.5 px-2 font-bold w-[12%]">{isHi ? 'प्राप्त दृष्टियां' : 'Aspects Received'}</th>
                  <th className="py-2.5 px-2 font-bold w-[10%]">{isHi ? 'SAV बिंदु' : 'SAV / BAV'}</th>
                  <th className="py-2.5 px-2 font-bold w-[8%] text-right">{isHi ? 'क्रिया' : 'Action'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-astro-border/60">
                {filteredHouses.map((b) => {
                  const isExpanded = expandedHouse === b.houseNumber;

                  return (
                    <React.Fragment key={b.houseNumber}>
                      <tr 
                        onClick={() => setExpandedHouse(isExpanded ? null : b.houseNumber)}
                        className={`hover:bg-astro-primary/5 transition-colors cursor-pointer ${
                          isExpanded ? 'bg-astro-primary/10' : ''
                        }`}
                      >
                        {/* House Title */}
                        <td className="py-2.5 px-3 font-bold text-astro-primary">
                          <div className="flex items-center gap-1.5">
                            <span className="w-5 h-5 rounded-md bg-astro-card border border-astro-border flex items-center justify-center text-[10px] text-astro-text font-mono font-bold">
                              {b.houseNumber}
                            </span>
                            <span>{isHi ? b.houseTitleHi : b.houseTitleEn}</span>
                          </div>
                        </td>

                        {/* Sign */}
                        <td className="py-2.5 px-2 font-semibold text-astro-text">
                          {getZodiacName(b.sign, lang)}
                        </td>

                        {/* Bhavesh Name + Role */}
                        <td className="py-2.5 px-2">
                          <div className="flex flex-col">
                            <span className="font-bold text-astro-primary">{b.bhavesh}</span>
                            <span className="text-[9.5px] text-astro-secondary-text font-mono font-semibold">
                              {isHi ? b.bhaveshFunctionalRole.titleHi : b.bhaveshFunctionalRole.titleEn}
                            </span>
                          </div>
                        </td>

                        {/* Bhavesh Placement Vector */}
                        <td className="py-2.5 px-2">
                          <div className="flex flex-col">
                            <span className={`px-2 py-0.5 rounded font-mono font-bold text-[11px] border w-fit ${
                              b.bhaveshPlacement.isOwnHouse
                                ? 'bg-emerald-50 text-emerald-900 border-emerald-300'
                                : 'bg-indigo-50 dark:bg-indigo-950/30 text-indigo-900 dark:text-indigo-200 border-indigo-200 dark:border-indigo-800'
                            }`}>
                              {isHi ? b.bhaveshPlacement.vectorHi : b.bhaveshPlacement.vectorEn}
                            </span>
                            <span className="text-[9.5px] text-astro-secondary-text mt-0.5">
                              {b.bhaveshPlacement.placedSign} ({b.bhaveshPlacement.distanceFromOwnHouse}{b.bhaveshPlacement.distanceFromOwnHouse === 1 ? 'st' : b.bhaveshPlacement.distanceFromOwnHouse === 2 ? 'nd' : b.bhaveshPlacement.distanceFromOwnHouse === 3 ? 'rd' : 'th'} from own)
                            </span>
                          </div>
                        </td>

                        {/* Bhavesh Dignity in Occupied Sign */}
                        <td className="py-2.5 px-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] border inline-block ${getDignityBadgeClass(b.bhaveshDignity.level)}`}>
                            {b.bhaveshDignity.isExalted && '↑ '}
                            {b.bhaveshDignity.isDebilitated && '↓ '}
                            {b.bhaveshDignity.level}
                          </span>
                        </td>

                        {/* Occupants */}
                        <td className="py-2.5 px-2">
                          {b.occupants.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {b.occupants.map(occ => (
                                <span key={occ.planet} className="px-1.5 py-0.2 rounded bg-indigo-50 dark:bg-indigo-950/40 text-indigo-900 dark:text-indigo-200 border border-indigo-200 font-bold text-[10px]">
                                  {occ.planet}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-[10px] text-astro-secondary-text italic">{isHi ? 'रिक्त' : 'Empty'}</span>
                          )}
                        </td>

                        {/* Inbound Aspects */}
                        <td className="py-2.5 px-2">
                          {b.aspectsReceived.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {b.aspectsReceived.map(asp => (
                                <span 
                                  key={asp.planet} 
                                  className={`px-1.5 py-0.2 rounded font-bold text-[10px] border ${
                                    asp.isBenefic ? 'bg-emerald-50 text-emerald-900 border-emerald-300' : 'bg-rose-50 text-rose-900 border-rose-300'
                                  }`}
                                  title={`${asp.aspectType} from H${asp.fromHouse}`}
                                >
                                  {asp.planet} (H{asp.fromHouse})
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-[10px] text-astro-secondary-text">{isHi ? 'कोई नहीं' : 'None'}</span>
                          )}
                        </td>

                        {/* SAV / BAV */}
                        <td className="py-2.5 px-2 font-mono text-[11px]">
                          <span className={`px-1.5 py-0.5 rounded font-bold border ${
                            b.bav.houseSavPoints >= 30 ? 'bg-emerald-50 text-emerald-900 border-emerald-300' :
                            b.bav.houseSavPoints >= 28 ? 'bg-teal-50 text-teal-900 border-teal-300' :
                            'bg-rose-50 text-rose-900 border-rose-300'
                          }`}>
                            {b.bav.houseSavPoints} / 28
                          </span>
                        </td>

                        {/* Action Link to House Inspector */}
                        <td className="py-2.5 px-2 text-right">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onSelectHouse) onSelectHouse(b.houseNumber);
                            }}
                            className="px-2 py-1 rounded bg-astro-card hover:bg-astro-primary hover:text-white border border-astro-border text-[10px] font-bold transition-colors inline-flex items-center gap-1 cursor-pointer"
                            title={isHi ? 'भाव विश्लेषक में खोलें' : 'Inspect House in Detail'}
                          >
                            <span>{isHi ? 'जांचें' : 'Inspect'}</span>
                            <ArrowRight className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>

                      {/* Expanded Drawer for House */}
                      {isExpanded && (
                        <tr className="bg-astro-primary/5">
                          <td colSpan={9} className="p-4 border-b border-astro-border">
                            <div className="space-y-2 text-xs">
                              <div className="flex items-center justify-between border-b border-astro-border/60 pb-2">
                                <span className="font-bold text-astro-primary text-xs">
                                  {isHi ? `भाव ${b.houseNumber} (${b.sanskritName}) विस्तृत शास्त्रीय विवरण:` : `House ${b.houseNumber} (${b.sanskritName}) Complete Vedic Synthesis:`}
                                </span>
                                {b.dashaActivation.isActive && (
                                  <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-900 border border-emerald-300 font-bold text-[10px]">
                                    ⚡ {isHi ? b.dashaActivation.roleHi : b.dashaActivation.roleEn}
                                  </span>
                                )}
                              </div>
                              <p className="text-astro-text text-[11px] leading-relaxed">
                                <strong className="text-astro-primary">{isHi ? 'भाव कारकत्व क्षेत्र: ' : 'Signification: '}</strong>
                                {isHi ? b.bhavaMeaningHi : b.bhavaMeaningEn}
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1 text-[10.5px]">
                                <div className="p-2 bg-white dark:bg-astro-card rounded-lg border border-astro-border">
                                  <span className="text-astro-secondary-text block text-[9.5px] uppercase">{isHi ? 'भावेश गति एवं अवस्था' : 'Lord Motion & Condition'}</span>
                                  <span className="font-semibold text-astro-text">{b.bhaveshCondition.statusSummaryEn}</span>
                                </div>
                                <div className="p-2 bg-white dark:bg-astro-card rounded-lg border border-astro-border">
                                  <span className="text-astro-secondary-text block text-[9.5px] uppercase">{isHi ? 'संबंधित वर्ग चक्र' : 'Harmonic Vargas'}</span>
                                  <span className="font-mono font-bold text-astro-primary">{b.relevantVargas.join(', ')}</span>
                                </div>
                                <div className="p-2 bg-white dark:bg-astro-card rounded-lg border border-astro-border">
                                  <span className="text-astro-secondary-text block text-[9.5px] uppercase">{isHi ? 'अष्टकवर्ग सामर्थ्य' : 'Ashtakavarga Evaluation'}</span>
                                  <span className="font-semibold text-emerald-800 dark:text-emerald-300">{isHi ? b.bav.evaluationHi : b.bav.evaluationEn}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
